import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Handler o STAŁEJ tożsamości, który zawsze woła najnowszą wersję przekazanej funkcji.
 *
 * ⭐ Po co: wiersz drzewka jest zapamiętany (`memo`), a zapamiętanie nic nie daje, gdy każdy render
 * rodzica podaje mu nową funkcję — 2026-08-26 jedno naciśnięcie strzałki przy 1700 wierszach
 * kosztowało ~520 ms, bo przerysowywało wszystkie wiersze. Zwykły `useCallback` wymagałby
 * wyliczania zależności dla zamknięć czytających parametry adresu i wiersze; jedna pominięta —
 * i handler widzi stary stan. Tu funkcja jest podmieniana w efekcie układu (zanim nadejdzie
 * jakiekolwiek zdarzenie po renderze), a wołający dostaje niezmienną otoczkę. Wzorzec „useEvent"
 * z propozycji React RFC.
 *
 * ⚠ Nie wołać w czasie renderu — otoczka może wtedy wskazywać poprzednią wersję. Wyłącznie ze
 * zdarzeń i efektów.
 */
export function useStableHandler<Args extends unknown[], Result>(
  handler: (...args: Args) => Result,
): (...args: Args) => Result {
  const latest = useRef(handler);

  useLayoutEffect(() => {
    latest.current = handler;
  });

  return useCallback((...args: Args) => latest.current(...args), []);
}
